import type {
  CreateSOPFormDTO,
  UpdateSOPFormDTO,
} from "@/modules/sop/domain/contracts";
import type {
  CreateSOPMappingDTO,
  SOPMappingDTO,
  SOPMappingQueryDTO,
  SOPUsageDTO,
  UpdateSOPMappingDTO,
} from "@/modules/sop/domain/contracts/mapping";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";
import type {
  CreateSOPServiceResult,
  DeleteSOPServiceResult,
  DuplicateSOPServiceResult,
  SOPDetailResult,
  SOPListResult,
  UpdateSOPServiceResult,
} from "../../domain/types";

const baseUrl = `${config.apiUrl}/sop`;

export const sopApi = {
  async getById(id: string) {
    return await fetcher.get<SOPDetailResult>(`${baseUrl}/${id}`);
  },

  async list(query: Partial<OffsetPageQueryDTO> = {}) {
    const params = new URLSearchParams();
    if (query.limit) params.set("limit", String(query.limit));
    if (query.offset) params.set("offset", String(query.offset));
    if (query.sort) params.set("sort", JSON.stringify(query.sort));
    if (query.filters) params.set("filters", JSON.stringify(query.filters));

    const url = `${baseUrl}?${params.toString()}`;
    const res = await fetcher.get<SOPListResult>(url);
    return res;
  },

  async create(input: CreateSOPFormDTO) {
    const res = await fetcher.post<CreateSOPServiceResult>(baseUrl, input);
    return res;
  },

  async update(id: string, input: UpdateSOPFormDTO) {
    const res = await fetcher.patch<UpdateSOPServiceResult>(
      `${baseUrl}/${id}`,
      input,
    );
    return res;
  },

  async toggleStatus(id: string) {
    const res = await fetcher.post<SOPDetailResult>(
      `${baseUrl}/${id}/toggle-status`,
    );
    return res;
  },

  async delete(id: string) {
    const res = await fetcher.delete<DeleteSOPServiceResult>(
      `${baseUrl}/${id}`,
    );
    return res;
  },

  async duplicate(id: string) {
    const res = await fetcher.post<DuplicateSOPServiceResult>(
      `${baseUrl}/${id}/duplicate`,
    );
    return res;
  },
};

export const sopMappingsApi = {
  async list(query: Partial<SOPMappingQueryDTO> = {}) {
    const params = new URLSearchParams();
    if (query.limit) params.set("limit", String(query.limit));
    if (query.offset) params.set("offset", String(query.offset));
    if (query.sort) params.set("sort", JSON.stringify(query.sort));
    if (query.filters) params.set("filters", JSON.stringify(query.filters));
    if (query.sopId) params.set("sopId", query.sopId);
    if (query.positionId) params.set("positionId", query.positionId);
    if (query.departmentId) params.set("departmentId", query.departmentId);
    if (query.search) params.set("search", query.search);

    const url = `${baseUrl}/mappings?${params.toString()}`;
    const res = await fetcher.get<{
      data: SOPMappingDTO[];
      meta: { total: number; limit: number; offset: number };
    }>(url);
    return res;
  },

  async get(id: string) {
    return await fetcher.get<SOPMappingDTO>(`${baseUrl}/mappings/${id}`);
  },

  async create(input: CreateSOPMappingDTO) {
    const res = await fetcher.post<SOPMappingDTO>(`${baseUrl}/mappings`, input);
    return res;
  },

  async update(id: string, input: UpdateSOPMappingDTO) {
    const res = await fetcher.patch<SOPMappingDTO>(
      `${baseUrl}/mappings/${id}`,
      input,
    );
    return res;
  },

  async delete(id: string) {
    const res = await fetcher.delete<{ deleted: boolean }>(
      `${baseUrl}/mappings/${id}`,
    );
    return res;
  },

  async getBySopId(sopId: string) {
    const res = await fetcher.get<{ data: SOPMappingDTO[] }>(
      `${baseUrl}/${sopId}/mappings`,
    );
    return res;
  },

  async getSopUsage(sopId: string) {
    return await fetcher.get<SOPUsageDTO>(`${baseUrl}/${sopId}/usage`);
  },
};
